import { Box, Card, CardContent, CircularProgress, Tooltip, Typography } from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid";
import FilterBar from "components/PageableTable/FilterBar";
import { RootState } from "config/store";
import supportedLocales from "config/supportedLocales";
import { format } from "date-fns";
import i18n from "i18n";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getFileImportPage } from "shared/network/file-import-log.api";

export type TableState = {
  page: number;
  pageSize: number;
  filterOpen: boolean;
  filterValue: string;
};

const FileLoglist = ({
  listType,
  selectedId,
  setSelectedId,
}: {
  listType: "TRANSACTION" | "RECEIPT";
  selectedId: number | null;
  setSelectedId: Dispatch<SetStateAction<number | null>>;
}) => {
  const { t }: any = useTranslation();
  const history = useHistory();
  const tenant = useSelector(
    (state: RootState) => state?.authentication?.selectedRelTenant?.tenant,
  );
  const [sortState, setSortState] = useState("");
  const [tableState, setTableState] = useState<TableState>({
    page: parseInt(window.sessionStorage.getItem("rap-invoice-transaction-page-number") || "0"),
    pageSize: parseInt(window.sessionStorage.getItem("rap-invoice-transaction-page-size") || "10"),
    filterOpen: false,
    filterValue: "",
  });
  const { page, pageSize, filterValue } = tableState;

  const getFileImportPageQuery = useQuery(
    ["getFileImportPageQuery", page, pageSize, sortState],
    async () => {
      const isReceiptList = listType === "RECEIPT";
      if (tenant?.id) {
        const { data } = await getFileImportPage(
          page,
          pageSize,
          tenant.id,
          (isReceiptList
            ? `fileImportType=RECEIPT_SUMMARY_IMPORT;`
            : `fileImportType!=RECEIPT_SUMMARY_IMPORT;`) + filterValue,
          // filterValue,
          sortState,
        );

        return data;
      }
      return Promise.reject();
    },
  );
  const items = getFileImportPageQuery.data?.page?.content || [];
  const isLoading = getFileImportPageQuery.isLoading;
  const isError = getFileImportPageQuery.isError;

  const columns: GridColDef[] = [
    {
      field: "filename",
      headerName: "Fájlnév",
      sortable: true,
      flex: 1,
    },
    {
      field: "fileImportType",
      headerName: "Fájl típusa",
      sortable: true,
      type: "select",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        switch (row.fileImportType) {
          case "OTP_BANK_IMPORT":
            return "OTP számlafájl";
          case "MBH_BANK_IMPORT":
            return "MBH számlafájl";
          case "RECEIPT_SUMMARY_IMPORT":
            return "Banki kivonat";
          default:
            return "Ismeretlen Típus";
        }
      },
    },

    {
      field: "importedAt",
      headerName: "Importálás dátuma",
      sortable: true,
      type: "date",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) =>
        format(new Date(row?.importedAt), "Pp", {
          locale: supportedLocales[i18n.language],
        }),
    },
    {
      field: "status",
      headerName: "Státusz",
      sortable: true,
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => {
        switch (row.status) {
          case "SUCCESS":
            return "Sikeres";
          case "ERROR":
            return "Hiba";
          default:
            return "Ismeretlen";
        }
      },
    },
  ];

  return (
    <Card>
      <Box
        pl={2}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        style={{
          background: "linear-gradient(90deg, rgba(25, 118, 210, 0.10) 0%, #FFFFFF 100%)",
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        <Typography variant="h6" style={{ margin: "16px 8px" }}>
          Importált fájlok
        </Typography>
      </Box>
      <FilterBar
        filterOptions={[
          {
            columnName: "fileImportType",
            options: ["OTP_BANK_IMPORT", "MBH_BANK_IMPORT", "RECEIPT_SUMMARY_IMPORT"].map(value => {
              let translated = "";
              switch (value) {
                case "OTP_BANK_IMPORT":
                  translated = "OTP számlafájl";
                  break;
                case "MBH_BANK_IMPORT":
                  translated = "MBH számlafájl";
                  break;
                case "RECEIPT_SUMMARY_IMPORT":
                  translated = "Banki kivonat";
                  break;
                default:
                  translated = "Ismeretlen Típus";
              }
              return {
                translated,
                value: value + "",
              };
            }),
          },
          {
            columnName: "status",
            options: ["ERROR", "SUCCESS"].map(value => {
              let translated = "";
              switch (value) {
                case "SUCCESS":
                  translated = "Sikeres";
                  break;
                case "ERROR":
                  translated = "Hiba";
                  break;
                default:
                  translated = "Ismeretlen";
                  break;
              }
              return {
                translated,
                value,
              };
            }),
          },
        ]}
        columns={columns}
        filterable={["filename", "fileImportType", "status", "importedAt"]}
        setTableState={setTableState}
        sessionStorageKey="file-log"
      />
      <Box p={2} pt={0}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box textAlign="center" mt={2} color="error.main">
            Hiba a fájl napló betöltésekor.
          </Box>
        ) : !items.length ? (
          <Box textAlign="center" mt={2} color="text.secondary">
            Nincs importált fájl.
          </Box>
        ) : (
          <Box>
            {items.map((row: any) => (
              <Card
                key={row.id}
                variant="outlined"
                onClick={() => setSelectedId(selectedId => (selectedId === row.id ? null : row.id))}
                style={{
                  borderRadius: 3,
                  boxShadow: "0 2px 8px rgba(25, 118, 210, 0.10)",
                  background: selectedId === row.id ? "#e3f2fd" : "#f7faff",
                  padding: 8,
                  marginBottom: 16,
                  cursor: "pointer",
                  border: selectedId === row.id ? "2px solid #1976d2" : undefined,
                  transition: "background 0.2s, border 0.2s",
                }}
              >
                <Typography variant="h6" style={{ fontSize: 16 }}>
                  {(() => {
                    switch (row.fileImportType) {
                      case "OTP_BANK_IMPORT":
                        return "OTP számlafájl";
                      case "MBH_BANK_IMPORT":
                        return "MBH számlafájl";
                      case "RECEIPT_SUMMARY_IMPORT":
                        return "Banki kivonat";
                      default:
                        return "Ismeretlen Típus";
                    }
                  })()}
                </Typography>
                <Tooltip title={row.filename} arrow>
                  <Typography
                    variant="body1"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                    }}
                  >
                    {row.filename}
                  </Typography>
                </Tooltip>
                <Typography variant="body2">
                  {format(new Date(row.importedAt), "Pp", {
                    locale: supportedLocales[i18n.language],
                  })}{" "}
                  &nbsp;|&nbsp;
                  {row.status === "SUCCESS"
                    ? "Sikeres"
                    : row.status === "ERROR"
                    ? "Hiba"
                    : "Ismeretlen"}
                </Typography>
              </Card>
            ))}
            {(getFileImportPageQuery.data?.page?.totalPages ?? 0) > 1 && (
              <Box display="flex" justifyContent="center" mt={2} width={"100%"}>
                <Pagination
                  count={getFileImportPageQuery.data?.page?.totalPages ?? 0}
                  page={page + 1}
                  onChange={(_, value) => setTableState(ts => ({ ...ts, page: value - 1 }))}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default FileLoglist;
